/**
 * /campaigns/:campaignId — le cœur de la campagne.
 *
 * Un code, N machines, un audit consolidé, une facture. La page montre, dans
 * cet ordre : le CODE (uniquement s'il vient d'être créé — il n'est jamais
 * relu depuis Firestore, où seule son empreinte existe), la commande à lancer,
 * le suivi machine par machine en temps réel, les machines manquantes, puis le
 * dossier consolidé dès qu'il existe.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Layers, Loader2, Lock } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { useT } from '@/lib/i18n';
import { watchCampaign, watchCampaignHosts } from '@/lib/firestore';
import { callCloseCampaign, callGetCampaign } from '@/lib/functions';
import {
  campaignCommand, campaignStatusKey, campaignStatusVariant, canCloseCampaign,
  hasConsolidation, missingCampaignHosts, type MissingCampaignHost,
} from '@/lib/campaigns';
import { fmtDate } from '@/lib/utils';
import type { CampaignDoc, CampaignHostDoc } from '@/types/models';
import { AlertBanner } from '@/components/AlertBanner';
import { BackLink } from '@/components/BackLink';
import { CampaignCodeCard, CampaignCodeLost, CampaignCommand } from '@/components/campaign/CampaignCode';
import { CampaignHostsTable, MissingHostsCard } from '@/components/campaign/CampaignHosts';
import { ConsolidatedPanel } from '@/components/campaign/ConsolidatedPanel';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Dialog } from '@/components/ui/dialog';
import { EmptyState } from '@/components/ui/empty-state';
import { Loader } from '@/components/ui/loader';

/** Le code n'arrive QUE de l'état de navigation posé par « Nouvelle campagne ». */
interface CampaignNavState { code?: string; expiresAt?: string }

export default function CampaignDetail() {
  const { campaignId } = useParams<{ campaignId: string }>();
  const { state } = useLocation();
  const { claims } = useAuth();
  const { t, locale } = useT();

  const navState = (state ?? null) as CampaignNavState | null;
  const code = navState?.code ?? null;

  // Chargement initial autoritatif (droits, machines manquantes calculées
  // côté serveur) puis temps réel : le snapshot prime dès qu'il arrive.
  const [initial, setInitial] = useState<{ campaign: CampaignDoc; hosts: CampaignHostDoc[]; missing: MissingCampaignHost[] } | null>(null);
  const [liveCampaign, setLiveCampaign] = useState<CampaignDoc | null>(null);
  const [liveHosts, setLiveHosts] = useState<CampaignHostDoc[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [confirmClose, setConfirmClose] = useState(false);
  const [closing, setClosing] = useState(false);

  const load = useCallback(() => {
    if (!campaignId) return;
    callGetCampaign({ campaignId })
      .then(({ data }) => setInitial({
        campaign: { ...data.campaign, id: campaignId },
        hosts: data.hosts ?? [],
        missing: data.missingHosts ?? [],
      }))
      .catch((e: Error) => setError(e.message));
  }, [campaignId]);

  useEffect(load, [load]);

  useEffect(() => {
    if (!campaignId) return;
    return watchCampaign(campaignId, c => { if (c) setLiveCampaign(c); }, e => setError(e.message));
  }, [campaignId]);

  useEffect(() => {
    if (!campaignId) return;
    return watchCampaignHosts(campaignId, setLiveHosts, e => setError(e.message));
  }, [campaignId]);

  const campaign = liveCampaign ?? initial?.campaign ?? null;
  const hosts = liveHosts ?? initial?.hosts ?? [];

  /**
   * Machines manquantes : recalculées sur le consolidé en temps réel dès qu'un
   * snapshot est arrivé, sinon la liste rendue par `getCampaign`. La commande
   * affichée est toujours celle de la campagne (`auditai --code …`).
   */
  const missing = useMemo<MissingCampaignHost[]>(() => {
    if (liveCampaign) {
      return missingCampaignHosts(liveCampaign.consolidated, liveCampaign.hostsExpected ?? [], code);
    }
    return (initial?.missing ?? []).map(m => ({ ...m, command: campaignCommand(code) }));
  }, [liveCampaign, initial, code]);

  const close = async () => {
    if (!campaignId) return;
    setClosing(true);
    try {
      await callCloseCampaign({ campaignId });
      toast.success(t('campaign.close.done'));
      setConfirmClose(false);
      load();
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setClosing(false);
    }
  };

  if (error && !campaign) {
    return (
      <div className="space-y-4">
        <BackLink to="/campaigns" label={t('campaign.back')} />
        <EmptyState
          icon={<Layers className="h-5 w-5" />}
          title={t('campaign.not_found')}
          description={t('campaign.not_found.desc')}
        />
        <AlertBanner variant="danger">{error}</AlertBanner>
      </div>
    );
  }
  if (!campaign) return <Loader fullscreen label={t('common.loading')} />;

  const consolidated = campaign.consolidated;
  const showConsolidated = hasConsolidation(consolidated);

  return (
    <div className="space-y-6">
      <BackLink to="/campaigns" label={t('campaign.back')} />

      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="font-display text-2xl text-ink-900 dark:text-white">{campaign.displayName}</h1>
            <Badge variant={campaignStatusVariant(campaign.status)}>{t(campaignStatusKey(campaign.status))}</Badge>
          </div>
          <p className="mt-1 text-sm text-ink-500">
            {t('campaign.detail.created', {
              when: fmtDate(campaign.createdAt, locale),
              who: campaign.createdByEmail ?? campaign.createdBy,
            })}
            {' · '}
            {t('campaign.detail.audits', { n: campaign.auditIds?.length ?? 0 })}
          </p>
          <p className="mt-1 text-xs text-ink-500">{t('campaign.detail.billed')}</p>
        </div>
        {canCloseCampaign(claims.role, campaign.status) && (
          <Button variant="destructiveOutline" onClick={() => setConfirmClose(true)}>
            <Lock className="h-4 w-4" /> {t('campaign.close')}
          </Button>
        )}
      </header>

      {campaign.status === 'closed' && campaign.closedAt && (
        <AlertBanner variant="info">{t('campaign.detail.closed_at', { when: fmtDate(campaign.closedAt, locale) })}</AlertBanner>
      )}

      <Card className="space-y-4">
        {code
          ? <CampaignCodeCard code={code} expiresAt={navState?.expiresAt ?? campaign.expiresAt} />
          : <CampaignCodeLost expiresAt={campaign.expiresAt} />}
        <CampaignCommand code={code} />
      </Card>

      <CampaignHostsTable hosts={hosts} />

      <MissingHostsCard missing={missing} />

      {showConsolidated && consolidated ? (
        <ConsolidatedPanel consolidated={consolidated} />
      ) : (
        <EmptyState
          icon={<Layers className="h-5 w-5" />}
          title={t('campaign.consolidated.none.title')}
          description={t('campaign.consolidated.none.desc')}
        />
      )}

      <Dialog
        open={confirmClose}
        onClose={() => setConfirmClose(false)}
        title={t('campaign.close.confirm.title')}
        footer={
          <>
            <Button variant="secondary" onClick={() => setConfirmClose(false)} disabled={closing}>{t('common.cancel')}</Button>
            <Button variant="destructive" onClick={close} disabled={closing}>
              {closing ? <Loader2 className="h-4 w-4 animate-spin" /> : null} {t('campaign.close.confirm.cta')}
            </Button>
          </>
        }
      >
        <p className="text-sm text-ink-600 dark:text-ink-300">{t('campaign.close.confirm.desc')}</p>
      </Dialog>
    </div>
  );
}
